import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, TextField, createFilterOptions } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';

export interface StudioOption {
  _id: string;
  studioName: string;
  city?: string;
}

// Search by name AND city, so "hyd" finds "Chakra · Hyderabad".
const filterStudios = createFilterOptions<StudioOption>({
  stringify: (o) => `${o.studioName ?? ''} ${o.city ?? ''}`,
});

interface StudioSelectorProps {
  value: StudioOption | null;
  onChange: (studio: StudioOption | null) => void;
  label?: string;
  /** Hides the "All studios" behaviour — the caller needs a studio to function. */
  required?: boolean;
  minWidth?: number;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

/**
 * The shared studio picker for super-admin pages.
 *
 * Extracted because every page that needed one re-implemented it locally
 * (PurchasedPacksPage, PurplePilatesOpsPage, ManualBookingDialog,
 * ManualPackAssignDialog, ClassFormDialog, EventFormDialog, CopyDayDialog…),
 * each with its own fetch, its own defensive unwrapping of the inconsistent
 * getStudios() response shape, and its own idea of what "is a studio role"
 * means. Merchandise needs one on four tabs, so it is worth having exactly one.
 *
 * Behaviour:
 *  - Super-admins get a searchable picker over all studios.
 *  - Studio-scoped roles get NOTHING rendered — their studio is pinned from the
 *    JWT and the server ignores any studioId they send. The component seeds
 *    `value` from auth on mount so the parent's data-fetch still has an id.
 *
 * That asymmetry is the whole point: the parent can pass `selectedStudio?._id`
 * as a query param unconditionally and be correct for both roles.
 */
const StudioSelector: React.FC<StudioSelectorProps> = ({
  value,
  onChange,
  label = 'Studio',
  required = false,
  minWidth = 200,
  size = 'small',
  disabled = false,
}) => {
  const { admin } = useAuth();
  const isStudio = admin?.role !== 'super_admin' && admin?.role !== 'admin';
  const [studios, setStudios] = useState<StudioOption[]>([]);
  const [loading, setLoading] = useState(false);

  // Studio roles: seed from auth once so the parent has an id to fetch with,
  // even though no picker is ever shown.
  useEffect(() => {
    if (isStudio && !value && admin?.studio?._id) {
      onChange({ _id: admin.studio._id, studioName: admin.studio.studioName ?? 'My studio' });
    }
    // Intentionally on mount / auth change only — re-running on `value` would
    // fight the parent whenever it legitimately clears the selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStudio, admin?.studio?._id]);

  const fetchStudios = useCallback(async () => {
    if (isStudio) return; // never needed, never fetch
    setLoading(true);
    try {
      const res: any = await apiService.getStudios({ pageSize: 200 });
      // getStudios() is unwrapped differently by every caller in this codebase.
      // Normalise here, once.
      const list: StudioOption[] =
        res?.data?.studios || res?.studios || res?.data || res || [];
      setStudios(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load studios', err);
      setStudios([]);
    } finally {
      setLoading(false);
    }
  }, [isStudio]);

  useEffect(() => {
    fetchStudios();
  }, [fetchStudios]);

  if (isStudio) return null;

  return (
    <Autocomplete
      size={size}
      options={studios}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(o) => o.studioName ?? ''}
      isOptionEqualToValue={(o, v) => o._id === v._id}
      filterOptions={filterStudios}
      // Keyed by id, not by label: MUI's default key is the label, and two
      // studios sharing a name ("Chakra" in Bengaluru and Hyderabad) collided —
      // React then reused rows while typing and showed ghost duplicates.
      // The city tells same-named studios apart.
      renderOption={(props, o) => {
        const { key: _labelKey, ...rest } = props as any;
        return (
          <Box component="li" key={o._id} {...rest}>
            {o.studioName}
            {o.city && (
              <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.85em' }}>
                · {o.city}
              </Box>
            )}
          </Box>
        );
      }}
      value={value}
      onChange={(_, next) => onChange(next)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={required ? 'Select a studio' : 'All studios'}
        />
      )}
      sx={{ minWidth }}
    />
  );
};

export default StudioSelector;
